import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag, Truck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import { money } from "../lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, subtotal, discount, afCount, lineId } = useCart();
  const navigate = useNavigate();
  const FREE = 150;
  const progress = Math.min(100, (subtotal / FREE) * 100);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[80]" data-testid="cart-drawer">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
            className="absolute right-0 top-0 bottom-0 w-full sm:w-[440px] bg-[#0B0B0C] border-l border-[rgba(212,175,55,0.25)] flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[rgba(212,175,55,0.18)]">
              <h3 className="font-serif-lux text-2xl text-[#FAFAF7]">Your Bag</h3>
              <button data-testid="cart-close" onClick={() => setOpen(false)}><X className="text-[#FAFAF7]/70 hover:text-[#D4AF37]" /></button>
            </div>

            <div className="px-6 py-4 border-b border-[rgba(212,175,55,0.12)]">
              <div className="flex items-center gap-2 text-xs text-[#FAFAF7]/70 mb-2">
                <Truck size={14} className="text-[#D4AF37]" />
                {subtotal >= FREE ? "You've unlocked complimentary luxury packaging & shipping" : `Add ${money(FREE - subtotal)} for free shipping & gift packaging`}
              </div>
              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#D4AF37] transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center gap-4">
                  <ShoppingBag size={40} className="text-[#D4AF37]/40" />
                  <p className="text-[#FAFAF7]/60 font-serif-lux text-xl">Your bag is empty</p>
                  <button onClick={() => { setOpen(false); navigate("/shop"); }} className="btn-outline-gold px-6 py-2 rounded-full text-sm">Discover Fragrances</button>
                </div>
              ) : (
                items.map((it) => {
                  const id = lineId(it);
                  return (
                    <div key={id} data-testid={`cart-item-${it.product_id}`} className="flex gap-4 py-4 border-b border-[rgba(212,175,55,0.1)]">
                      <img src={it.image} alt={it.name} className="w-20 h-24 object-cover rounded" />
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <p className="font-serif-lux text-lg text-[#FAFAF7] leading-tight">{it.name}</p>
                          <button data-testid={`cart-remove-${it.product_id}`} onClick={() => removeItem(id)}><Trash2 size={15} className="text-[#FAFAF7]/40 hover:text-[#58111A]" /></button>
                        </div>
                        {it.volume && <p className="text-xs text-[#FAFAF7]/50 mt-0.5">{it.volume}</p>}
                        {it.engraving && <p className="text-xs text-[#D4AF37]/70 mt-0.5 italic">Engraved: "{it.engraving}"</p>}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-3 gold-border rounded-full px-3 py-1">
                            <button data-testid={`cart-dec-${it.product_id}`} onClick={() => updateQty(id, it.quantity - 1)}><Minus size={13} className="text-[#FAFAF7]/70" /></button>
                            <span className="text-sm text-[#FAFAF7] w-4 text-center">{it.quantity}</span>
                            <button data-testid={`cart-inc-${it.product_id}`} onClick={() => updateQty(id, it.quantity + 1)}><Plus size={13} className="text-[#FAFAF7]/70" /></button>
                          </div>
                          <span className="text-[#D4AF37]">{money(it.price * it.quantity)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {items.length > 0 && (
              <div className="px-6 py-5 border-t border-[rgba(212,175,55,0.18)]">
                {afCount > 0 && afCount % 3 !== 0 && (
                  <p data-testid="af-deal-hint" className="text-xs text-[#D4AF37] mb-3">Add {3 - (afCount % 3)} more air freshener{3 - (afCount % 3) > 1 ? "s" : ""} to get 3 for $25</p>
                )}
                {discount > 0 && (
                  <div className="flex justify-between mb-2 text-sm">
                    <span className="text-emerald-400">Air Fresheners · 3 for $25</span>
                    <span className="text-emerald-400" data-testid="cart-discount">−{money(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between mb-4">
                  <span className="text-[#FAFAF7]/70">Subtotal</span>
                  <span className="font-serif-lux text-xl text-[#FAFAF7]" data-testid="cart-subtotal">{money(subtotal - discount)}</span>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => { setOpen(false); navigate("/checkout"); }}
                  className="btn-gold w-full py-3.5 rounded-full text-sm">
                  Proceed to Checkout
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
